import type { ChangeEvent, ReactNode } from "react";

export type SwotFieldOption = {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean | undefined;
  readonly description?: string | undefined;
};

export type SwotFieldDefinition = {
  readonly name: string;
  readonly label: string;
  readonly type:
    | "text"
    | "textarea"
    | "number"
    | "date"
    | "datetime"
    | "select"
    | "multiselect"
    | "checkbox"
    | "radio"
    | "file"
    | "readonly"
    | "hidden";
  readonly required?: boolean | undefined;
  readonly disabled?: boolean | undefined;
  readonly readOnly?: boolean | undefined;
  readonly placeholder?: string | undefined;
  readonly description?: string | undefined;
  readonly options?: readonly SwotFieldOption[] | undefined;
  readonly min?: number | string | undefined;
  readonly max?: number | string | undefined;
  readonly maxLength?: number | undefined;
  readonly accept?: string | undefined;
  readonly multiple?: boolean | undefined;
  readonly autoComplete?: string | undefined;
};

export type SwotFieldRendererProps = {
  readonly field: SwotFieldDefinition;
  readonly value?: unknown;
  readonly checked?: boolean | undefined;
  readonly error?: ReactNode | undefined;
  readonly onChange?: ((fieldName: string, value: unknown) => void) | undefined;
  readonly onBlur?: ((fieldName: string) => void) | undefined;
};

function scalarValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function arrayValue(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function boolValue(value: unknown, checked?: boolean): boolean {
  if (checked !== undefined) return checked;
  if (typeof value === "boolean") return value;
  return false;
}

function commonInputProps(field: SwotFieldDefinition, error: ReactNode | undefined) {
  const describedBy = [field.description ? `${field.name}-help` : undefined, error ? `${field.name}-error` : undefined].filter(Boolean).join(" ") || undefined;
  return {
    id: field.name,
    name: field.name,
    required: field.required,
    disabled: field.disabled,
    readOnly: field.readOnly,
    placeholder: field.placeholder,
    autoComplete: field.autoComplete,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy,
  };
}

export function SwotFieldRenderer(props: SwotFieldRendererProps) {
  const { field } = props;
  if (field.type === "hidden") {
    return <input type="hidden" name={field.name} value={scalarValue(props.value)} />;
  }

  const options = field.options ?? [];
  const label = (
    <label className="swot-field-renderer__label" htmlFor={field.name}>
      {field.label}{field.required ? <span aria-label="required"> *</span> : null}
    </label>
  );
  const help = field.description ? <p id={`${field.name}-help`} className="swot-field-renderer__help">{field.description}</p> : null;
  const error = props.error ? <div id={`${field.name}-error`} className="swot-field-renderer__error">{props.error}</div> : null;
  const common = commonInputProps(field, props.error);

  const onScalarChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => props.onChange?.(field.name, event.currentTarget.value);
  const onBlur = () => props.onBlur?.(field.name);
  let control: ReactNode;

  switch (field.type) {
    case "textarea":
      control = <textarea {...common} value={scalarValue(props.value)} maxLength={field.maxLength} onChange={onScalarChange} onBlur={onBlur} />;
      break;
    case "number":
      control = <input {...common} type="number" value={scalarValue(props.value)} min={field.min} max={field.max} maxLength={field.maxLength} onChange={onScalarChange} onBlur={onBlur} />;
      break;
    case "date":
      control = <input {...common} type="date" value={scalarValue(props.value)} min={field.min} max={field.max} onChange={onScalarChange} onBlur={onBlur} />;
      break;
    case "datetime":
      control = <input {...common} type="datetime-local" value={scalarValue(props.value)} min={field.min} max={field.max} onChange={onScalarChange} onBlur={onBlur} />;
      break;
    case "select":
      control = (
        <select {...common} value={scalarValue(props.value)} onChange={onScalarChange} onBlur={onBlur}>
          <option value="">Select {field.label}</option>
          {options.map((option) => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
        </select>
      );
      break;
    case "multiselect": {
      const selected = arrayValue(props.value);
      control = (
        <select {...common} multiple value={[...selected]} onChange={(event) => props.onChange?.(field.name, Array.from(event.currentTarget.selectedOptions).map((option) => option.value))} onBlur={onBlur}>
          {options.map((option) => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
        </select>
      );
      break;
    }
    case "checkbox":
      control = <input {...common} type="checkbox" checked={boolValue(props.value, props.checked)} onChange={(event) => props.onChange?.(field.name, event.currentTarget.checked)} onBlur={onBlur} />;
      break;
    case "radio":
      control = (
        <div className="swot-field-renderer__radio-group" role="radiogroup" aria-labelledby={`${field.name}-legend`}>
          <span id={`${field.name}-legend`} className="swot-field-renderer__sr-only">{field.label}</span>
          {options.map((option) => (
            <label key={option.value} className="swot-field-renderer__radio-option">
              <input type="radio" name={field.name} value={option.value} checked={scalarValue(props.value) === option.value} disabled={field.disabled || option.disabled} onChange={onScalarChange} onBlur={onBlur} />
              {option.label}
            </label>
          ))}
        </div>
      );
      break;
    case "file":
      control = <input {...common} type="file" accept={field.accept} multiple={field.multiple} onChange={(event) => props.onChange?.(field.name, field.multiple ? event.currentTarget.files : event.currentTarget.files?.[0] ?? null)} onBlur={onBlur} />;
      break;
    case "readonly":
      control = <output id={field.name} className="swot-field-renderer__readonly">{scalarValue(props.value)}</output>;
      break;
    case "text":
    default:
      control = <input {...common} type="text" value={scalarValue(props.value)} maxLength={field.maxLength} onChange={onScalarChange} onBlur={onBlur} />;
      break;
  }

  return (
    <div className={`swot-field-renderer swot-field-renderer--${field.type}`} data-field-name={field.name}>
      {label}
      {control}
      {help}
      {error}
    </div>
  );
}
